import { computed } from "vue"
import { useViewStore } from "@/stores/viewStore"
import type { ParcelBooleanFilterField, View, ViewScope } from "@/types"
import type { TransactionVerificationStatus } from "@/types"

type RangeValue = [number, number]
type DateRangeValue = { min: Date | null; max: Date | null }
type OpportunityFilter = NonNullable<NonNullable<View["filter"]>["opportunity"]>
type TransactionFilter = NonNullable<NonNullable<View["filter"]>["transaction"]>
type BuyerFilter = NonNullable<NonNullable<View["filter"]>["buyer"]>
type BuyerTransactionFilter = NonNullable<BuyerFilter["transaction"]>
type ParcelFilter = NonNullable<NonNullable<View["filter"]>["parcel"]>

function dateString(date: Date | null): string | null {
	return date ? date.toISOString().split("T")[0] : null
}

export function useView(scope: ViewScope) {
	const viewStore = useViewStore()
	const view = computed(() => viewStore.view(scope) ?? { scope })

	function patchOpportunityFilter(patch: Partial<OpportunityFilter>): void {
		viewStore.patchView(scope, {
			filter: {
				...view.value.filter,
				opportunity: {
					...view.value.filter?.opportunity,
					...patch,
				},
			},
		})
	}

	function patchParcelFilter(patch: Partial<ParcelFilter>): void {
		viewStore.patchView(scope, {
			filter: {
				...view.value.filter,
				parcel: {
					...view.value.filter?.parcel,
					...patch,
				},
			},
		})
	}

	function patchTransactionFilter(patch: Partial<TransactionFilter>): void {
		viewStore.patchView(scope, {
			filter: {
				...view.value.filter,
				transaction: {
					...view.value.filter?.transaction,
					...patch,
				},
			},
		})
	}

	function patchBuyerFilter(patch: Partial<BuyerFilter>): void {
		viewStore.patchView(scope, {
			filter: {
				...view.value.filter,
				buyer: {
					...view.value.filter?.buyer,
					...patch,
				},
			},
		})
	}

	function patchBuyerTransactionFilter(patch: Partial<BuyerTransactionFilter>): void {
		patchBuyerFilter({
			transaction: {
				...view.value.filter?.buyer?.transaction,
				...patch,
			},
		})
	}

	function setRange(field: "price" | "landArea" | "buildableLandArea" | "residentialUnits", range: RangeValue): void {
		patchOpportunityFilter({ [field]: { min: range[0], max: range[1] } })
	}

	function setTransactionRange(
		field: "price" | "landArea" | "buildableLandArea" | "residentialUnits",
		range: RangeValue
	): void {
		patchTransactionFilter({ [field]: { min: range[0], max: range[1] } })
	}

	function resetFilter(): void {
		viewStore.patchView(scope, { filter: undefined })
	}

	function setOrder(order: View["order"]): void {
		viewStore.patchView(scope, { order })
	}

	function setSelect(select: View["select"]): void {
		viewStore.patchView(scope, { select })
	}

	function setColumnOverrides(columnOverrides: View["columnOverrides"]): void {
		viewStore.patchView(scope, { columnOverrides })
	}

	return {
		view,
		setOrder,
		setSelect,
		setColumnOverrides,
		setOpportunityTypes: (opportunityTypes: string[]) => patchOpportunityFilter({ opportunityTypes }),
		setAssetTypes: (assetTypes: string[]) => patchOpportunityFilter({ assetTypes }),
		setLandCategories: (landCategories: string[]) => patchOpportunityFilter({ landCategories }),
		setLandSubcategories: (landSubcategories: string[]) => patchOpportunityFilter({ landSubcategories }),
		setRegions: (regions: string[]) => patchOpportunityFilter({ regions }),
		setMunicipalities: (municipalities: string[]) => patchOpportunityFilter({ municipalities }),
		setBoroughs: (boroughs: string[]) => patchOpportunityFilter({ boroughs }),
		setEvaluationStatus: (evaluationStatus: string[]) => patchOpportunityFilter({ evaluationStatus }),
		setPriority: (priority: string | null) =>
			patchOpportunityFilter({ priority: priority === "all" ? null : priority }),
		setBrokers: (brokers: string[]) => patchOpportunityFilter({ brokers }),
		setPriceRange: (range: RangeValue) => setRange("price", range),
		setLandAreaRange: (range: RangeValue) => setRange("landArea", range),
		setBuildableLandAreaRange: (range: RangeValue) => setRange("buildableLandArea", range),
		setResidentialUnitsRange: (range: RangeValue) => setRange("residentialUnits", range),
		setLaunchDateRange: (range: DateRangeValue) =>
			patchOpportunityFilter({
				launchDate: {
					min: dateString(range.min),
					max: dateString(range.max),
				},
			}),
		setTransactionAssetTypes: (assetTypes: string[]) => patchTransactionFilter({ assetTypes }),
		setTransactionLandCategories: (landCategories: string[]) => patchTransactionFilter({ landCategories }),
		setTransactionLandSubcategories: (landSubcategories: string[]) => patchTransactionFilter({ landSubcategories }),
		setTransactionStatuses: (statuses: string[]) => patchTransactionFilter({ statuses }),
		setTransactionVerificationStatus: (verificationStatus: TransactionVerificationStatus | null) =>
			patchTransactionFilter({ verificationStatus }),
		setTransactionBuyerStatus: (buyerStatus: string | null) => patchTransactionFilter({ buyerStatus }),
		setTransactionSource: (source: string | null) => patchTransactionFilter({ source }),
		setTransactionComposition: (composition: string | null) => patchTransactionFilter({ composition }),
		setTransactionPriceRange: (range: RangeValue) => setTransactionRange("price", range),
		setTransactionLandAreaRange: (range: RangeValue) => setTransactionRange("landArea", range),
		setTransactionBuildableLandAreaRange: (range: RangeValue) => setTransactionRange("buildableLandArea", range),
		setTransactionResidentialUnitsRange: (range: RangeValue) => setTransactionRange("residentialUnits", range),
		setTransactionDateRange: (range: DateRangeValue) =>
			patchTransactionFilter({
				date: {
					min: dateString(range.min),
					max: dateString(range.max),
				},
			}),
		setTransactionRegions: (regions: string[]) => patchTransactionFilter({ regions }),
		setTransactionMunicipalities: (municipalities: string[]) => patchTransactionFilter({ municipalities }),
		setTransactionBoroughs: (boroughs: string[]) => patchTransactionFilter({ boroughs }),
		setTransactionSearch: (search: string) => patchTransactionFilter({ search: search || null }),
		setBuyerHasContactDetails: (hasContactDetails: boolean) => patchBuyerFilter({ hasContactDetails }),
		setBuyerTransactionAssetTypes: (assetTypes: string[]) => patchBuyerTransactionFilter({ assetTypes }),
		setBuyerTransactionLandCategories: (landCategories: string[]) => patchBuyerTransactionFilter({ landCategories }),
		setBuyerTransactionLandSubcategories: (landSubcategories: string[]) =>
			patchBuyerTransactionFilter({ landSubcategories }),
		setBuyerTransactionStatuses: (statuses: string[]) => patchBuyerTransactionFilter({ statuses }),
		setBuyerTransactionVerificationStatus: (verificationStatus: TransactionVerificationStatus | null) =>
			patchBuyerTransactionFilter({ verificationStatus }),
		setBuyerTransactionBuyerStatus: (buyerStatus: string | null) => patchBuyerTransactionFilter({ buyerStatus }),
		setBuyerTransactionSource: (source: string | null) => patchBuyerTransactionFilter({ source }),
		setBuyerTransactionComposition: (composition: string | null) => patchBuyerTransactionFilter({ composition }),
		setBuyerTransactionPriceRange: (range: RangeValue) =>
			patchBuyerTransactionFilter({ price: { min: range[0], max: range[1] } }),
		setBuyerTransactionLandAreaRange: (range: RangeValue) =>
			patchBuyerTransactionFilter({ landArea: { min: range[0], max: range[1] } }),
		setBuyerTransactionBuildableLandAreaRange: (range: RangeValue) =>
			patchBuyerTransactionFilter({ buildableLandArea: { min: range[0], max: range[1] } }),
		setBuyerTransactionResidentialUnitsRange: (range: RangeValue) =>
			patchBuyerTransactionFilter({ residentialUnits: { min: range[0], max: range[1] } }),
		setBuyerTransactionDateRange: (range: DateRangeValue) =>
			patchBuyerTransactionFilter({
				date: {
					min: dateString(range.min),
					max: dateString(range.max),
				},
			}),
		setBuyerTransactionRegions: (regions: string[]) => patchBuyerTransactionFilter({ regions }),
		setBuyerTransactionMunicipalities: (municipalities: string[]) => patchBuyerTransactionFilter({ municipalities }),
		setBuyerTransactionBoroughs: (boroughs: string[]) => patchBuyerTransactionFilter({ boroughs }),
		setParcelSuperficyRange: (range: RangeValue) => patchParcelFilter({ superficy: { min: range[0], max: range[1] } }),
		setParcelDwellingUnitsRange: (range: RangeValue) =>
			patchParcelFilter({ dwellingUnits: { min: range[0], max: range[1] } }),
		setParcelStoreysRange: (range: RangeValue) => patchParcelFilter({ storeys: { min: range[0], max: range[1] } }),
		setParcelPropertyValueRange: (range: RangeValue) =>
			patchParcelFilter({ propertyValue: { min: range[0], max: range[1] } }),
		setParcelBuildingAgeRange: (range: RangeValue) =>
			patchParcelFilter({ buildingAge: { min: range[0], max: range[1] } }),
		setParcelShowStreets: (showStreets: boolean) => patchParcelFilter({ showStreets }),
		setParcelBooleanFilter: (field: ParcelBooleanFilterField, value: boolean | null) =>
			patchParcelFilter({ [field]: value }),
		resetFilter,
	}
}
